import { useCallback, useMemo } from 'react';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import {
  canAccessItem,
  IBasePreferenceItem,
  IPreferenceItem,
  isPreferenceActive,
  preferenceItems,
  resolveAccessiblePreferences,
} from '@/creditflowresearch/application/hooks/userPreferences/helpers';
import { getMergedSegmentItems } from '@/creditflowresearch/application/hooks/userPreferences/segmentPreferences';

const useGetItemsUserPreferences = () => {
  const { preferences, products, regions } = useUserPlanContext();

  const canAccess = useCallback(
    (item: IPreferenceItem) => canAccessItem(item, products, regions),
    [products, regions],
  );

  const accessiblePreferences = useMemo(
    () => resolveAccessiblePreferences(preferenceItems, preferences, canAccess),
    [preferences, canAccess],
  );

  const canActive = useCallback((item: IBasePreferenceItem) => {
    return isPreferenceActive(accessiblePreferences, item);
  }, [accessiblePreferences]);

  const currentChildren = useMemo(
    () => preferenceItems.find((item) => canActive(item))?.children || [],
    [canActive],
  );

  const segmentItems = useMemo(
    () => getMergedSegmentItems(accessiblePreferences),
    [accessiblePreferences],
  );

  return {
    items: preferenceItems,
    currentChildren,
    segmentItems,
    canAccess,
    canActive,
    accessiblePreferences,
  };
};

export default useGetItemsUserPreferences;
